import React from "react";
import { Loader2, Save } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { inputClass, labelClass, btnPrimary } from "@/components/Brand";
import { MandatoryPrintPicker } from "@/components/MandatoryPrintPicker";
import { PrintSizePicker } from "@/components/PrintSizePicker";

/** Editable project settings: client details, quotas, module and print quotas. */
export const ProjectSettingsForm = ({ form, setForm, busy, onSubmit, printCatalog }) => {
  const { t } = useI18n();
  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <form onSubmit={onSubmit} className="lg:col-span-3 border border-[#2A2A2A] bg-[#0F0F0F] p-6 lg:p-8 space-y-6">
      <p className="eyebrow">{t("edit_quota")} & {t("print_quotas")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className={labelClass}>{t("project_code")}</label>
          <input data-testid="edit-code-input" className={inputClass} value={form.code} onChange={(e) => set({ code: e.target.value })} />
        </div>
        <div>
          <label className={labelClass}>{t("project_title")}</label>
          <input data-testid="edit-title-input" className={inputClass} value={form.title} onChange={(e) => set({ title: e.target.value })} required />
        </div>
        <div>
          <label className={labelClass}>{t("client_name")}</label>
          <input data-testid="edit-client-name-input" className={inputClass} value={form.client_name} onChange={(e) => set({ client_name: e.target.value })} />
        </div>
        <div>
          <label className={labelClass}>{t("client_email")}</label>
          <input data-testid="edit-client-email-input" type="email" className={inputClass} value={form.client_email} onChange={(e) => set({ client_email: e.target.value })} />
        </div>
        <div>
          <label className={labelClass}>{t("client_phone")}</label>
          <input data-testid="edit-client-phone-input" className={inputClass} value={form.client_phone}
            onChange={(e) => set({ client_phone: e.target.value })} placeholder="08123456789" />
        </div>
        <div>
          <label className={labelClass}>{t("extra_price_override")}</label>
          <input data-testid="edit-extra-price-input" type="number" className={inputClass} value={form.extra_edit_price}
            onChange={(e) => set({ extra_edit_price: e.target.value })} placeholder="5000" />
        </div>
      </div>
      <div>
        <label className={labelClass}>{t("drive_link")}</label>
        <input data-testid="edit-drive-link-input" className={inputClass} value={form.drive_link} onChange={(e) => set({ drive_link: e.target.value })} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className={labelClass}>{t("edit_quota")}</label>
          <input data-testid="edit-edit-quota-input" type="number" min="1" className={inputClass} value={form.edit_quota} onChange={(e) => set({ edit_quota: e.target.value })} />
        </div>
        <div>
          <label className={labelClass}>{t("max_extra")}</label>
          <input data-testid="edit-max-extra-input" type="number" min="0" className={inputClass} value={form.max_extra_edits} onChange={(e) => set({ max_extra_edits: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className={labelClass}>{t("package_price")}</label>
          <input data-testid="edit-package-price-input" type="number" min="0" className={inputClass}
            value={form.package_price} onChange={(e) => set({ package_price: e.target.value })} placeholder="750000" />
        </div>
        <div>
          <label className={labelClass}>{t("package_label")}</label>
          <input data-testid="edit-package-label-input" className={inputClass} value={form.package_label}
            onChange={(e) => set({ package_label: e.target.value })} placeholder={t("package_label_placeholder")} />
        </div>
      </div>
      <div data-testid="edit-module-toggle">
        <label className={labelClass}>{t("project_module")}</label>
        <div className="mt-3 grid grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
          {[
            { key: "edit_print", label: t("module_edit_print") },
            { key: "edit_only", label: t("module_edit_only") },
          ].map((m) => (
            <button key={m.key} type="button" data-testid={`edit-module-${m.key}-btn`}
              onClick={() => set({ module: m.key })}
              className={`py-3 min-h-[44px] text-[10px] tracking-[0.16em] uppercase transition-colors ${
                form.module === m.key ? "bg-[#D4AF37] text-black" : "bg-[#0B0B0B] text-[#A8A8A8] hover:text-[#F3F3F1]"
              }`}>
              {m.label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-[#707070]">{t("module_hint")}</p>
      </div>
      {form.module === "edit_print" && (
        <PrintSizePicker catalog={printCatalog} selected={form.print_sizes}
          onChange={(sizes) => setForm((f) => ({ ...f, print_sizes: sizes }))}
          testIdPrefix="edit-print-size" />
      )}
      {form.module === "edit_print" && (
        <MandatoryPrintPicker catalog={printCatalog} items={form.print_quota_items}
          onChange={(items) => setForm((f) => ({ ...f, print_quota_items: items }))}
          testIdPrefix="edit-mandatory-print" />
      )}
      <button data-testid="save-project-btn" disabled={busy === "save"} className={btnPrimary}>
        {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {t("save_changes")}
      </button>
    </form>
  );
};
